/**
 * The nine lifecycle actions, as data — ARCHITECTURE §05.4.
 *
 * **Why a registry and not nine dialogs.** Nine hand-written dialogs is nine
 * chances to omit a confirmation, and the one that gets omitted will be the one
 * nobody exercised. Here, an action that fails to declare a `consequence` or an
 * `eligibleStatuses` list is a **type error**, and `actionRegistry.spec.ts`
 * fails if a tenth action is added without one. The dialog, the action bar and
 * the bulk bar all read this table, so there is exactly one place where
 * "disconnect is destructive" is written down.
 *
 * `permission` and `feature` are imported types from `@rd/shared`, never string
 * literals, so a key renamed in the contract breaks this build rather than
 * silently un-gating a control.
 *
 * Paths, permissions and features are transcribed from
 * `apps/api/src/modules/sims/sims.routes.ts` — the route table is the authority
 * and this is the client's copy of it.
 */

import type { FeatureKey, PermissionKey, SimSummary } from '@rd/shared';

export type SimActionKey =
  | 'activate'
  | 'activateTest'
  | 'activateFromTest'
  | 'suspend'
  | 'resume'
  | 'disconnect'
  | 'disconnectResume'
  | 'changePlan'
  | 'swap';

export interface SimActionDef {
  key: SimActionKey;
  /** Imperative, as it appears on a button. */
  label: string;
  /**
   * The word the operator must type to confirm an irreversible action.
   * Upper-case, matched exactly — see §05.4.
   */
  confirmWord: string;
  /** POST path under `/api/v1`. */
  path: string;
  permission: PermissionKey;
  feature: FeatureKey;
  /** Danger colours and type-to-confirm. Only the genuinely irreversible. */
  destructive: boolean;
  /** May be applied to a multi-SIM selection. */
  bulk: boolean;
  requiresPlan: boolean;
  requiresReason: boolean;
  offersConsent: boolean;
  /**
   * One sentence, present tense, naming the real-world effect — not the API
   * call. This is the first thing in the dialog and the whole point of §05.4.
   */
  consequence: string;
  /**
   * SIM statuses this action may be offered for, upper-cased for comparison.
   * Empty means "always offered".
   *
   * §13.8: the upstream never documents the wire spelling of a SIM status, so
   * these are the values observed in responses. An unrecognised status makes an
   * action **ineligible** rather than silently allowed — the safe direction.
   */
  eligibleStatuses: readonly string[];
}

export const SIM_ACTIONS: Readonly<Record<SimActionKey, SimActionDef>> = {
  activate: {
    key: 'activate',
    label: 'Activate',
    confirmWord: 'ACTIVATE',
    path: '/sims/activate',
    permission: 'sims.activate',
    feature: 'sims.lifecycle.activate',
    destructive: false,
    bulk: true,
    requiresPlan: true,
    requiresReason: false,
    offersConsent: false,
    consequence:
      'These SIMs start carrying traffic on the plan you choose, and billing for that plan begins.',
    // INITIAL is the live gateway's spelling for an un-activated SIM; without
    // it the activation picker offers nothing against a real account, whose
    // 12,751 available SIMs all report INITIAL.
    eligibleStatuses: ['AVAILABLE', 'INACTIVE', 'INITIAL'],
  },
  activateTest: {
    key: 'activateTest',
    label: 'Activate in test mode',
    confirmWord: 'TEST',
    path: '/sims/activate-test',
    permission: 'sims.activate_test',
    feature: 'sims.lifecycle.test_mode',
    destructive: false,
    bulk: true,
    requiresPlan: true,
    requiresReason: false,
    offersConsent: false,
    consequence:
      'These SIMs start carrying traffic under a test allowance, so you can verify a device before billing starts.',
    eligibleStatuses: ['AVAILABLE', 'INACTIVE'],
  },
  activateFromTest: {
    key: 'activateFromTest',
    label: 'Move to a live plan',
    confirmWord: 'ACTIVATE',
    path: '/sims/activate-from-test',
    permission: 'sims.activate',
    feature: 'sims.lifecycle.activate',
    destructive: false,
    bulk: true,
    requiresPlan: true,
    requiresReason: false,
    offersConsent: true,
    consequence:
      'These SIMs leave test mode and move onto the live plan you choose. Billing for that plan begins now.',
    eligibleStatuses: ['TEST_MODE', 'TESTMODE'],
  },
  suspend: {
    key: 'suspend',
    label: 'Put in safe custody',
    confirmWord: 'SUSPEND',
    path: '/sims/suspend',
    permission: 'sims.suspend',
    feature: 'sims.lifecycle.safe_custody',
    destructive: true,
    bulk: true,
    requiresPlan: false,
    requiresReason: false,
    offersConsent: false,
    consequence:
      'These SIMs stop carrying traffic immediately. Any device using one loses its connection until you take it out of safe custody.',
    eligibleStatuses: ['ACTIVE'],
  },
  resume: {
    key: 'resume',
    label: 'Take out of safe custody',
    confirmWord: 'RESUME',
    path: '/sims/resume',
    permission: 'sims.resume',
    feature: 'sims.lifecycle.safe_custody',
    destructive: false,
    bulk: true,
    requiresPlan: false,
    requiresReason: false,
    offersConsent: false,
    consequence: 'These SIMs start carrying traffic again on their existing plan.',
    eligibleStatuses: ['SAFE_CUSTODY', 'SAFECUSTODY', 'SUSPENDED'],
  },
  disconnect: {
    key: 'disconnect',
    label: 'Disconnect',
    confirmWord: 'DISCONNECT',
    path: '/sims/disconnect',
    permission: 'sims.disconnect',
    feature: 'sims.lifecycle.disconnect',
    destructive: true,
    bulk: true,
    requiresPlan: false,
    requiresReason: true,
    offersConsent: true,
    consequence:
      'These SIMs stop carrying traffic immediately, and are permanently disconnected after 5 days. After that they cannot be reconnected from this panel.',
    eligibleStatuses: ['ACTIVE', 'TEST_MODE', 'TESTMODE'],
  },
  disconnectResume: {
    key: 'disconnectResume',
    label: 'Reconnect',
    confirmWord: 'RECONNECT',
    path: '/sims/disconnect/resume',
    permission: 'sims.disconnect',
    feature: 'sims.lifecycle.disconnect',
    destructive: false,
    bulk: true,
    requiresPlan: false,
    requiresReason: false,
    offersConsent: false,
    consequence:
      'These SIMs start carrying traffic again. This only works within 5 days of disconnection.',
    eligibleStatuses: ['TEMP_DISCONNECTED', 'TEMPDISCONNECTED', 'DISCONNECTED'],
  },
  changePlan: {
    key: 'changePlan',
    label: 'Change plan',
    confirmWord: 'CHANGE',
    path: '/sims/change-plan',
    permission: 'sims.change_plan',
    feature: 'sims.plan_upgrade',
    destructive: false,
    bulk: true,
    requiresPlan: true,
    requiresReason: false,
    offersConsent: true,
    consequence:
      'These SIMs move onto the plan you choose. Billing changes to the new plan, and the change cannot be undone from this panel without another plan change.',
    eligibleStatuses: ['ACTIVE'],
  },
  swap: {
    key: 'swap',
    label: 'Swap SIM',
    confirmWord: 'SWAP',
    path: '/sims/swap',
    permission: 'sims.swap',
    feature: 'sims.lifecycle.swap',
    destructive: true,
    // The API takes `{ existingIccid, newIccid }`, not an items[] array, so this
    // is single-SIM by construction and never appears in the bulk bar.
    bulk: false,
    requiresPlan: false,
    requiresReason: false,
    offersConsent: false,
    consequence:
      'The number moves to the new SIM. The old SIM stops working immediately and cannot be used again.',
    eligibleStatuses: ['ACTIVE', 'SAFE_CUSTODY', 'SAFECUSTODY'],
  },
};

/** Stable display order for the action bar: additive first, destructive last. */
export const ACTION_ORDER: readonly SimActionKey[] = [
  'activate',
  'activateTest',
  'activateFromTest',
  'resume',
  'disconnectResume',
  'changePlan',
  'swap',
  'suspend',
  'disconnect',
];

/** Normalises a status for comparison: the wire spelling varies (§13.8). */
export function normaliseStatus(status: string | null | undefined): string {
  return (status ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');
}

export function isEligible(action: SimActionDef, sim: Pick<SimSummary, 'status'>): boolean {
  if (action.eligibleStatuses.length === 0) return true;
  return action.eligibleStatuses.includes(normaliseStatus(sim.status));
}

export interface ActionGate {
  hasPermission(...keys: PermissionKey[]): boolean;
  hasFeature(...keys: FeatureKey[]): boolean;
}

/**
 * The actions to offer for one SIM.
 *
 * **Presentation only.** Hiding an action the user could not perform is a
 * courtesy; the API refuses it independently on every request, and criterion 15
 * asserts exactly that by issuing the call anyway.
 */
export function actionsFor(sim: Pick<SimSummary, 'status'>, gate: ActionGate): SimActionDef[] {
  return ACTION_ORDER.map((key) => SIM_ACTIONS[key]).filter(
    (action) =>
      gate.hasPermission(action.permission) &&
      gate.hasFeature(action.feature) &&
      isEligible(action, sim),
  );
}

/** The actions offerable to a whole selection, with how much of it each covers. */
export interface BulkActionOffer {
  action: SimActionDef;
  eligible: SimSummary[];
  ineligible: SimSummary[];
}

export function bulkActionsFor(sims: SimSummary[], gate: ActionGate): BulkActionOffer[] {
  return ACTION_ORDER.map((key) => SIM_ACTIONS[key])
    .filter(
      (action) =>
        action.bulk && gate.hasPermission(action.permission) && gate.hasFeature(action.feature),
    )
    .map((action) => ({
      action,
      eligible: sims.filter((sim) => isEligible(action, sim)),
      ineligible: sims.filter((sim) => !isEligible(action, sim)),
    }))
    // An action that applies to none of the selection is not offered at all.
    .filter((offer) => offer.eligible.length > 0);
}
